import { BASE_URL, HERO_VIDEO, ORGANIZATION } from './constants';

export type BreadcrumbItem = { name: string; url: string };

export function buildPostalAddressSchema() {
  return {
    '@type': 'PostalAddress' as const,
    streetAddress: ORGANIZATION.address.streetAddress,
    addressLocality: ORGANIZATION.address.addressLocality,
    addressRegion: ORGANIZATION.address.addressRegion,
    addressCountry: ORGANIZATION.address.addressCountry,
  };
}

export function buildGeoCoordinatesSchema() {
  return {
    '@type': 'GeoCoordinates' as const,
    latitude: ORGANIZATION.geo.latitude,
    longitude: ORGANIZATION.geo.longitude,
  };
}

export function buildOpeningHoursSchema() {
  return {
    '@type': 'OpeningHoursSpecification' as const,
    dayOfWeek: [...ORGANIZATION.openingHours.dayOfWeek],
    opens: ORGANIZATION.openingHours.opens,
    closes: ORGANIZATION.openingHours.closes,
  };
}

/** Organization reference by stable @id (for nested provider/publisher). */
export function buildOrganizationReference() {
  return {
    '@type': 'Organization' as const,
    '@id': ORGANIZATION.id,
    name: ORGANIZATION.name,
    url: ORGANIZATION.url,
  };
}

/**
 * Single LocalBusiness + MedicalBusiness + Organization entity.
 * Use once sitewide — do not emit a second Organization block.
 */
export function buildLocalBusinessOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'MedicalBusiness', 'Organization'],
    '@id': ORGANIZATION.id,
    name: ORGANIZATION.name,
    alternateName: ORGANIZATION.alternateName,
    url: ORGANIZATION.url,
    image: ORGANIZATION.image,
    logo: ORGANIZATION.logo,
    telephone: ORGANIZATION.telephone,
    email: ORGANIZATION.email,
    priceRange: '$$',
    currenciesAccepted: 'AED',
    paymentAccepted: 'Cash, Credit Card, Insurance',
    address: buildPostalAddressSchema(),
    geo: buildGeoCoordinatesSchema(),
    openingHoursSpecification: [buildOpeningHoursSchema()],
    sameAs: [...ORGANIZATION.sameAs],
    areaServed: [
      {
        '@type': 'City',
        name: 'Dubai',
        '@id': 'https://www.wikidata.org/wiki/Q613',
      },
      {
        '@type': 'Country',
        name: 'United Arab Emirates',
        '@id': 'https://www.wikidata.org/wiki/Q878',
      },
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: ORGANIZATION.telephone,
        contactType: 'customer service',
        email: ORGANIZATION.email,
        areaServed: ['AE', 'Dubai'],
        availableLanguage: ['en', 'ar'],
        hoursAvailable: buildOpeningHoursSchema(),
      },
    ],
    description:
      'The Speech Clinic Dubai is a pediatric therapy center in Dubai Healthcare City, providing speech therapy, occupational therapy, physical therapy, and feeding therapy for children.',
    foundingDate: '2020',
    foundingLocation: {
      '@type': 'Place',
      name: 'Dubai, United Arab Emirates',
    },
    slogan: 'Innovative Leaders In Paediatric Therapy',
    knowsAbout: [
      'Pediatric Speech Therapy',
      'Occupational Therapy',
      'Physical Therapy',
      'Feeding Therapy',
      'Autism Therapy',
      'Child Development',
      'Sensory Integration',
      'Pediatric Rehabilitation',
    ],
    medicalSpecialty: [
      'Speech-Language Pathology',
      'Occupational Therapy',
      'Physical Therapy',
      'Pediatric Rehabilitation',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Pediatric Therapy Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Speech and Language Therapy',
            description: 'Pediatric speech and language therapy services in Dubai',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Occupational Therapy',
            description: 'Pediatric occupational therapy services in Dubai',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Physical Therapy',
            description: 'Pediatric physical therapy services in Dubai',
          },
        },
      ],
    },
  };
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function buildMedicalServiceSchema({
  name,
  description,
  url,
}: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalService',
    name,
    description,
    url,
    provider: {
      '@type': 'MedicalOrganization',
      '@id': ORGANIZATION.id,
      name: ORGANIZATION.name,
      url: ORGANIZATION.url,
      telephone: ORGANIZATION.telephone,
      address: buildPostalAddressSchema(),
    },
    areaServed: {
      '@type': 'Country',
      name: 'United Arab Emirates',
    },
    medicalSpecialty: 'Pediatric Therapy',
  };
}

export function buildVideoObjectSchema({
  name,
  description,
  contentUrl,
  thumbnailUrl,
  uploadDate = '2023-03-20T00:00:00+04:00',
  duration,
  pageUrl,
}: {
  name: string;
  description: string;
  contentUrl: string;
  thumbnailUrl: string;
  uploadDate?: string;
  duration?: string;
  pageUrl: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name,
    description,
    thumbnailUrl: [thumbnailUrl],
    uploadDate: toIsoDateTime(uploadDate),
    contentUrl,
    ...(duration ? { duration } : {}),
    embedUrl: pageUrl,
    publisher: {
      '@type': 'Organization',
      '@id': ORGANIZATION.id,
      name: ORGANIZATION.name,
      logo: {
        '@type': 'ImageObject',
        url: ORGANIZATION.logo,
      },
    },
  };
}

export function buildHeroVideoSchema() {
  return buildVideoObjectSchema({
    ...HERO_VIDEO,
    pageUrl: BASE_URL,
  });
}

export function buildJobPostingSchema({
  title,
  description,
  employmentType = 'FULL_TIME',
  datePosted,
  validThrough,
  url,
}: {
  title: string;
  description: string;
  employmentType?: 'FULL_TIME' | 'PART_TIME' | 'CONTRACTOR';
  datePosted: string;
  validThrough: string;
  url: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title,
    description,
    datePosted,
    validThrough,
    employmentType,
    hiringOrganization: {
      '@type': 'Organization',
      '@id': ORGANIZATION.id,
      name: ORGANIZATION.name,
      sameAs: [...ORGANIZATION.sameAs],
      logo: ORGANIZATION.logo,
    },
    jobLocation: {
      '@type': 'Place',
      address: buildPostalAddressSchema(),
    },
    applicantLocationRequirements: {
      '@type': 'Country',
      name: 'United Arab Emirates',
    },
    directApply: true,
    url,
  };
}

export function buildWebPageSchema({
  name,
  description,
  url,
}: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url,
    isPartOf: {
      '@type': 'WebSite',
      name: ORGANIZATION.name,
      url: BASE_URL,
    },
    publisher: buildOrganizationReference(),
  };
}

export function toIsoDate(date: string | undefined): string {
  if (!date) return new Date().toISOString().split('T')[0];
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return new Date().toISOString().split('T')[0];
  return parsed.toISOString().split('T')[0];
}

/** Gulf Standard Time. Dubai does not observe daylight saving. */
const CLINIC_TIMEZONE = '+04:00';
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_TIME_NO_TZ = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)$/;
const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:\d{2})$/;

/** ISO 8601 datetime with a timezone, as required for VideoObject.uploadDate. */
export function toIsoDateTime(value: string | undefined, fallback = '2023-03-20'): string {
  const raw = (value || fallback).trim();

  if (HAS_TIMEZONE.test(raw) && !Number.isNaN(Date.parse(raw))) return raw;

  const dateOnly = raw.match(DATE_ONLY);
  if (dateOnly) return `${raw}T00:00:00${CLINIC_TIMEZONE}`;

  const dateTime = raw.match(DATE_TIME_NO_TZ);
  if (dateTime) {
    const withSeconds = raw.length === 16 ? `${raw}:00` : raw;
    return `${withSeconds}${CLINIC_TIMEZONE}`;
  }

  const parsed = Date.parse(raw);
  if (!Number.isNaN(parsed)) return new Date(parsed).toISOString();

  return `${fallback}T00:00:00${CLINIC_TIMEZONE}`;
}

export function buildBlogPostingSchema({
  title,
  description,
  slug,
  imageUrl,
  datePublished,
  dateModified,
  authorName,
  keywords,
}: {
  title: string;
  description: string;
  slug: string;
  imageUrl: string;
  datePublished: string;
  dateModified?: string;
  authorName: string;
  keywords?: string[];
}) {
  const pageUrl = `${BASE_URL}/blogs/${slug}`;
  const published = toIsoDate(datePublished);
  const modified = toIsoDate(dateModified || datePublished);

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description,
    image: {
      '@type': 'ImageObject',
      url: imageUrl,
      width: 1200,
      height: 630,
    },
    datePublished: published,
    dateModified: modified,
    author: {
      '@type': 'Organization',
      '@id': ORGANIZATION.id,
      name: authorName || ORGANIZATION.name,
      url: ORGANIZATION.url,
    },
    publisher: {
      '@type': 'Organization',
      '@id': ORGANIZATION.id,
      name: ORGANIZATION.name,
      logo: {
        '@type': 'ImageObject',
        url: ORGANIZATION.logo,
      },
      url: ORGANIZATION.url,
    },
    keywords: keywords?.length ? keywords.join(', ') : undefined,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
    url: pageUrl,
    inLanguage: 'en-AE',
    articleSection: 'Pediatric Therapy',
  };
}

export function buildMedicalWebPageSchema({
  name,
  description,
  url,
  dateModified,
}: {
  name: string;
  description: string;
  url: string;
  dateModified: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name,
    description,
    url,
    dateModified: toIsoDate(dateModified),
    inLanguage: 'en-AE',
    isPartOf: {
      '@type': 'WebSite',
      name: ORGANIZATION.name,
      url: BASE_URL,
    },
    publisher: buildOrganizationReference(),
    about: {
      '@type': 'MedicalOrganization',
      '@id': ORGANIZATION.id,
      name: ORGANIZATION.name,
    },
    mainEntityOfPage: url,
  };
}

export function buildFaqPageSchema(
  faqs: { question: string; answer: string }[]
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}
